import React, { SetStateAction, useState, Dispatch } from 'react';
import { View, StyleSheet, TouchableOpacity, TextInput, ScrollView } from 'react-native';
import { ThemeConsumer, Theme } from '@rneui/themed';
import { ZenIcon, ZenText } from 'zen-core-elements';
import CommonService from '../../../../shared/services/commonService';
import { PAYMENT_METHODS } from '../../../../redux/utils/discountUtils';
import { paymentSaveType } from '../../../../redux/types/sellScreenTypes';
import { useAppSelector } from '../../../../redux/hooks';

interface RefundPaymentProps {
	paymentMethod: typeof PAYMENT_METHODS[0]["id"];
	totalAmount: number;
	customerDetails?: any;
	onClose: () => void;
	handleRefund: (value: paymentSaveType) => void;
	setReason: Dispatch<SetStateAction<string>>;
	reason: string
}

const RefundPayment: React.FC<RefundPaymentProps> = ({
	paymentMethod,
	totalAmount,
	customerDetails,
	onClose,
	reason,
	setReason,
	handleRefund,
}) => {
	const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'

	const selectedMethodObj = PAYMENT_METHODS.find((item) => item.id === paymentMethod) || {
		id: paymentMethod,
		icon: 'cash',
		color: '#10b981',
	};

	const formattedAmount = CommonService?.currencyStandard(currencySymbol, Math.abs(totalAmount || 0).toString());

	const onConfirmRefund = () => {
		handleRefund({
			amount: Math.abs(totalAmount || 0),
			paidAmount: Math.abs(totalAmount || 0),
			reason: reason.trim(),
		});
	};

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme);
				return (
					<ScrollView style={styles.container} showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 20 }} >
						{/* Modal Header */}
						<View style={styles.header}>
							<View style={styles.headerLeft}>
								<View style={styles.refundTag}>
									<ZenIcon name="rotate-left" type="material-community" size={14} color="#dc2626" />
									<ZenText content="RETURN REFUND" style={styles.refundTagText} />
								</View>
								<ZenText content="Process Refund" style={styles.headerTitle} />
							</View>
							{/* <TouchableOpacity onPress={onClose} style={styles.closeBtn} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
								<ZenIcon name="close" type="antdesign" color={theme?.lightDark} size={22} />
							</TouchableOpacity> */}
						</View>

						<ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
							{/* Refund Hero Card */}
							<View style={styles.heroCard}>
								<View style={styles.heroTopRow}>
									<View style={[styles.methodBadge, { backgroundColor: selectedMethodObj.color + '15', borderColor: selectedMethodObj.color + '30' }]}>
										<ZenIcon name={selectedMethodObj.icon} type="material-community" size={18} color={selectedMethodObj.color} />
										<ZenText content={selectedMethodObj.id} style={[styles.methodBadgeText, { color: selectedMethodObj.color }]} />
									</View>
									<ZenText content="REFUND AMOUNT" style={styles.heroAmountLabel} />
								</View>

								<View style={styles.amountContainer}>
									<ZenText content={formattedAmount} style={styles.heroAmountText} />
								</View>

								{customerDetails?.firstName || customerDetails?.name || customerDetails?.phone ? (
									<View style={styles.customerRow}>
										<ZenIcon name="account-outline" type="material-community" size={16} color={theme?.grey || '#6b7280'} />
										<ZenText
											content={`Customer: ${customerDetails?.firstName || customerDetails?.name || customerDetails?.phone}`}
											style={styles.customerText}
										/>
									</View>
								) : null}
							</View>

							{/* Refund Reason Note Input */}
							<View style={styles.inputSection}>
								<ZenText content="Refund Reason / Note (Optional)" style={styles.inputLabel} />
								<View style={styles.inputWrapper}>
									<ZenIcon name="notebook-outline" type="material-community" size={20} color={theme?.grey || '#9ca3af'} style={styles.inputIcon} />
									<TextInput
										style={styles.textInput}
										placeholder="e.g., Customer requested return, damaged item..."
										placeholderTextColor="#9ca3af"
										value={reason}
										onChangeText={setReason}
										multiline
									/>
								</View>
							</View>

							{/* Summary Details Box */}
							{/* <View style={styles.infoBox}>
								<View style={styles.infoRow}>
									<ZenText content="Payment Mode" style={styles.infoRowLabel} />
									<ZenText content={selectedMethodObj.id} style={styles.infoRowValue} />
								</View>
								<View style={styles.infoDivider} />
								<View style={styles.infoRow}>
									<ZenText content="Transaction Status" style={styles.infoRowLabel} />
									<View style={styles.statusBadge}>
										<View style={styles.statusDot} />
										<ZenText content="Ready to Refund" style={styles.statusText} />
									</View>
								</View>
							</View> */}
						</ScrollView>

						{/* Footer Actions */}
						<View style={styles.footer}>
							{/* <TouchableOpacity style={styles.cancelBtn} onPress={onClose}>
								<ZenText content="Cancel" style={styles.cancelBtnText} />
							</TouchableOpacity> */}

							<TouchableOpacity style={styles.confirmBtn} onPress={onConfirmRefund}>
								<ZenIcon name="check-circle-outline" type="material-community" size={20} color="#ffffff" style={{ marginRight: 6 }} />
								<ZenText content="Confirm Refund" style={styles.confirmBtnText} />
							</TouchableOpacity>
						</View>
					</ScrollView>
				);
			}}
		</ThemeConsumer>
	);
};

const style = (theme: Theme) =>
	StyleSheet.create({
		container: {
			paddingHorizontal: 4,
			paddingVertical: 4,
		},
		header: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'center',
			paddingBottom: 16,
			borderBottomWidth: 1,
			borderBottomColor: theme?.softSlate || '#f3f4f6',
			marginBottom: 16,
		},
		headerLeft: {
			flexDirection: 'column',
			gap: 4,
		},
		refundTag: {
			flexDirection: 'row',
			alignItems: 'center',
			alignSelf: 'flex-start',
			gap: 4,
			backgroundColor: '#fee2e2',
			paddingHorizontal: 8,
			paddingVertical: 3,
			borderRadius: 6,
		},
		refundTagText: {
			fontSize: 10,
			fontFamily: theme?.bold || 'System',
			color: '#dc2626',
			letterSpacing: 0.5,
		},
		headerTitle: {
			fontSize: theme?.headerFontSize || 20,
			fontFamily: theme?.bold || 'System',
			color: theme?.dark || '#111827',
		},
		closeBtn: {
			width: 36,
			height: 36,
			borderRadius: 18,
			backgroundColor: theme?.softSlate || '#f3f4f6',
			justifyContent: 'center',
			alignItems: 'center',
		},
		scrollContent: {
			gap: 16,
			paddingBottom: 12,
		},
		heroCard: {
			backgroundColor: '#fef2f2',
			borderRadius: 16,
			padding: 18,
			borderWidth: 1,
			borderColor: '#fecdd3',
		},
		heroTopRow: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'center',
			marginBottom: 12,
		},
		methodBadge: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: 6,
			paddingHorizontal: 10,
			paddingVertical: 5,
			borderRadius: 20,
			borderWidth: 1,
		},
		methodBadgeText: {
			fontSize: theme?.smallerFontSize || 12,
			fontFamily: theme?.bold || 'System',
		},
		heroAmountLabel: {
			fontSize: 11,
			fontFamily: theme?.medium || 'System',
			color: '#991b1b',
			letterSpacing: 0.5,
		},
		amountContainer: {
			marginVertical: 4,
		},
		heroAmountText: {
			fontSize: 32,
			fontFamily: theme?.bold || 'System',
			color: '#dc2626',
		},
		customerRow: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: 6,
			marginTop: 10,
			paddingTop: 10,
			borderTopWidth: 1,
			borderTopColor: '#fca5a550',
		},
		customerText: {
			fontSize: theme?.smallerFontSize || 12,
			fontFamily: theme?.medium || 'System',
			color: '#7f1d1d',
		},
		inputSection: {
			gap: 6,
		},
		inputLabel: {
			fontSize: theme?.smallFontSize || 13,
			fontFamily: theme?.medium || 'System',
			color: theme?.lightDark || '#374151',
		},
		inputWrapper: {
			flexDirection: 'row',
			alignItems: 'flex-start',
			borderWidth: 1,
			borderColor: theme?.softSlate || '#e5e7eb',
			borderRadius: 12,
			backgroundColor: theme?.light || '#ffffff',
			paddingHorizontal: 12,
			paddingVertical: 10,
			minHeight: 70,
		},
		inputIcon: {
			marginTop: 2,
			marginRight: 8,
		},
		textInput: {
			flex: 1,
			fontSize: theme?.smallFontSize || 14,
			fontFamily: theme?.medium,
			color: theme?.dark || '#111827',
			padding: 0,
			textAlignVertical: 'top',
		},
		infoBox: {
			backgroundColor: theme?.iceWhite || '#f9fafb',
			borderRadius: 12,
			padding: 14,
			borderWidth: 1,
			borderColor: theme?.softSlate || '#f3f4f6',
		},
		infoRow: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'center',
		},
		infoRowLabel: {
			fontSize: theme?.smallerFontSize || 13,
			fontFamily: theme?.medium || 'System',
			color: theme?.grey || '#6b7280',
		},
		infoRowValue: {
			fontSize: theme?.smallerFontSize || 13,
			fontFamily: theme?.bold || 'System',
			color: theme?.dark || '#111827',
		},
		infoDivider: {
			height: 1,
			backgroundColor: theme?.softSlate || '#f3f4f6',
			marginVertical: 10,
		},
		statusBadge: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: 6,
			backgroundColor: '#ecfdf5',
			paddingHorizontal: 8,
			paddingVertical: 3,
			borderRadius: 12,
		},
		statusDot: {
			width: 6,
			height: 6,
			borderRadius: 3,
			backgroundColor: '#10b981',
		},
		statusText: {
			fontSize: 11,
			fontFamily: theme?.bold || 'System',
			color: '#059669',
		},
		footer: {
			flexDirection: 'row',
			justifyContent: 'flex-end',
			alignItems: 'center',
			gap: 12,
			marginTop: 16,
			paddingTop: 14,
			borderTopWidth: 1,
			borderTopColor: theme?.softSlate || '#f3f4f6',
		},
		cancelBtn: {
			paddingVertical: 12,
			paddingHorizontal: 20,
			borderRadius: 10,
			borderWidth: 1,
			borderColor: theme?.softSlate || '#d1d5db',
			backgroundColor: theme?.light || '#ffffff',
		},
		cancelBtnText: {
			fontSize: theme?.smallFontSize || 14,
			fontFamily: theme?.medium || 'System',
			color: theme?.lightDark || '#374151',
		},
		confirmBtn: {
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			paddingVertical: 12,
			paddingHorizontal: 24,
			borderRadius: 10,
			backgroundColor: '#dc2626',
		},
		confirmBtnText: {
			fontSize: theme?.smallFontSize || 14,
			fontFamily: theme?.bold || 'System',
			color: '#ffffff',
		},
	});

export default RefundPayment;
